import { useState, useEffect } from "react";
import { Box, SimpleGrid, Text } from "@chakra-ui/react";
import FadingImage from "../common/FadingImage";
import { GreenIconBadge } from "./GreenIconBadge";
import { ConcentricCircles } from "./ConcentricCircles";
import { TbCertificate, TbCurrencyDollar, TbCalendarTime, TbRosette } from "react-icons/tb";
import { motion, type Variants } from "framer-motion";

const MotionBox = motion.create(Box);

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

interface ImageItem {
  src: string;
  objectPosition?: {
    base?: string;
    tablet?: string;
    desktop?: string;
  };
}

const images: ImageItem[] = [
  {
    src: "/home/cleaner1.webp",
    objectPosition: {
      base: "center 20%",
      tablet: "center 25%",
      desktop: "center 30%",
    },
  },
  {
    src: "/home/cleaner2.webp",
    objectPosition: {
      base: "center center",
      tablet: "center 35%",
      desktop: "center 40%",
    },
  },
  {
    src: "/home/cleaner3.webp",
    objectPosition: {
      base: "center 20%",
      tablet: "center 15%",
      desktop: "center 10%",
    },
  },
  {
    src: "/home/cleaner4.webp",
    objectPosition: {
      base: "center 10%",
      tablet: "center 15%",
      desktop: "center 20%",
    },
  },
  {
    src: "/home/cleaner5.webp",
    objectPosition: {
      base: "center 10%",
      tablet: "center 20%",
      desktop: "center 30%",
    },
  },
];

const items = [
  {
    title: "Verified Professionals",
    description:
      "Trained and dependable service teams you can trust in your home.",
    icon: TbCertificate,
  },
  {
    title: "Transparent Pricing",
    description:
      "Clear and straightforward pricing with no confusing surprises.",
    icon: TbCurrencyDollar,
  },
  {
    title: "Convenient Booking",
    description: "Book quickly online with hassle-free and smooth experience.",
    icon: TbCalendarTime,
  },
  {
    title: "Consistent Quality",
    description:
      "Reliable service standards with attention to detail every time.",
    icon: TbRosette,
  },
];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.4, delay: index * 0.15, ease },
  }),
};

export default function WhyChooseUs() {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 4200);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <Box
      w="100vw"
      position="relative"
      left="50%"
      ml="-50vw"
      overflow="hidden"
      id="why-homehelpy"
      bg="linear-gradient(180deg, #ffffff 0%, #f7fbf7 100%)"
      pt={{ base: "2.5rem", tablet: "3rem", desktop: "3.5rem" }}
      pb={{ base: "4rem", tablet: "5rem", desktop: "6rem" }}
    >
      <ConcentricCircles corner="top-left" />

      <Box
        maxW="1380px"
        mx="auto"
        px={{ base: "1rem", tablet: "1.5rem", desktop: "2rem" }}
        position="relative"
        zIndex={1}
      >
        {/* Section header */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          textAlign="center"
          mb={{ base: "1.5rem", tablet: "2rem", desktop: "2.25rem" }}
        >
          <Text
            color="primary.surface.default"
            fontWeight="800"
            letterSpacing="0.08em"
            fontSize={{ base: "1.2rem", tablet: "1.4rem", desktop: "1.5rem" }}
            textTransform="uppercase"
          >
            Why HomeHelpy
          </Text>

          <Box
            mt="0.9rem"
            mx="auto"
            w={{ base: "60px", tablet: "78px" }}
            h="3px"
            rounded="full"
            bg="linear-gradient(90deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,1) 100%)"
          />

          <Text
            mt="1rem"
            fontSize={{ base: "2rem", tablet: "2.5rem", desktop: "3.05rem" }}
            fontWeight="800"
            color="greyscale.text.title"
            lineHeight="1.08"
            letterSpacing="-0.02em"
          >
            Built around{" "}
            <Box as="span" color="primary.surface.default">
              trust
            </Box>
            {", "}comfort, and{" "}
            <Box as="span" color="primary.surface.default">
              consistency
            </Box>
          </Text>

          <Text
            mt="0.95rem"
            maxW="700px"
            mx="auto"
            color="greyscale.text.body"
            fontSize={{ base: "0.96rem", tablet: "1rem", desktop: "1.02rem" }}
            lineHeight="1.85"
          >
            Experience dependable home services built around trust, comfort, and
            a consistently smooth customer experience.
          </Text>
        </MotionBox>

        {/* Image + cards */}
        <Box position="relative">
          <MotionBox
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, ease }}
            position="relative"
            minH={{
              base: "18rem",
              sm: "24rem",
              tablet: "32rem",
              desktop: "36rem",
            }}
            rounded={{ base: "1.5rem", tablet: "1.75rem", desktop: "2rem" }}
            overflow="hidden"
            boxShadow="0 26px 80px rgba(16,24,40,0.1)"
          >
            {images.map((img, i) => (
              <Box
                key={img.src}
                position="absolute"
                inset="0"
                style={{
                  opacity: i === currentImage ? 1 : 0,
                  transform: i === currentImage ? "scale(1)" : "scale(1.035)",
                  transition: "opacity 1.15s ease, transform 2s ease",
                }}
              >
                <FadingImage
                  src={img.src}
                  alt="HomeHelpy professional cleaning service"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  loading="lazy"
                  objectPosition={{
                    base: img.objectPosition?.base ?? "center center",
                    tablet:
                      img.objectPosition?.tablet ??
                      img.objectPosition?.base ??
                      "center center",
                    desktop:
                      img.objectPosition?.desktop ??
                      img.objectPosition?.tablet ??
                      img.objectPosition?.base ??
                      "center center",
                  }}
                />
              </Box>
            ))}

            <Box
              position="absolute"
              inset="0"
              bg="linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.04) 40%, rgba(0,0,0,0.42) 100%)"
              zIndex={1}
              pointerEvents="none"
            />
          </MotionBox>

          {/* Cards:
              base + sm  → below the image, 2 columns
              tablet+    → absolute at image bottom, 4 columns */}
          <Box
            position={{ base: "relative", sm: "relative", tablet: "absolute" }}
            left={{ tablet: "50%" }}
            bottom={{ tablet: "1.25rem", desktop: "1.75rem" }}
            transform={{ tablet: "translateX(-50%)" }}
            w={{
              base: "100%",
              tablet: "calc(100% - 2.5rem)",
              desktop: "calc(100% - 3.5rem)",
            }}
            mt={{ base: "1rem", sm: "1.25rem", tablet: "0" }}
            zIndex={3}
          >
            <SimpleGrid
              columns={{ base: 2, sm: 2, tablet: 4 }}
              gap={{ base: "0.75rem", tablet: "1rem", desktop: "1rem" }}
            >
              {items.map((item, index) => (
                <MotionBox
                  key={item.title}
                  custom={index}
                  variants={itemVariants}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.25 }}
                  h="100%"
                  pt={{ base: "1.4rem", tablet: "0" }}
                >
                  <FeatureOverlayCard
                    title={item.title}
                    description={item.description}
                    icon={item.icon}
                  />
                </MotionBox>
              ))}
            </SimpleGrid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FeatureOverlayCard({
  title,
  description,
  icon,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
}) {
  return (
    <Box
      h="100%"
      position="relative"
      rounded="1.2rem"
      px={{ base: "0.85rem", desktop: "1.1rem" }}
      pt="1.2rem"
      pb="1.1rem"
      bg="rgba(255,255,255,0.82)"
      backdropFilter="blur(10px)"
      border="1px solid rgba(255,255,255,0.45)"
      boxShadow="0 14px 32px rgba(16,24,40,0.16)"
      textAlign="center"
      transition="all 0.25s ease"
      _hover={{
        transform: "translateY(-3px)",
        boxShadow: "0 20px 40px rgba(16,24,40,0.2)",
      }}
    >
      {/* Icon — half-outside at top center */}
      <GreenIconBadge
        icon={icon}
        outerSize={{ base: "3rem", desktop: "3.4rem" }}
        iconBoxSize={{ base: 5, desktop: 6 }}
        position="absolute"
        top="-1.5rem"
        left="50%"
        transform="translateX(-50%)"
        zIndex={1}
      />

      {/* Centered text */}
      <Box pt="1.2rem">
        <Text
          fontSize={{ base: "0.85rem", tablet: "0.88rem", desktop: "0.95rem" }}
          fontWeight="800"
          color="greyscale.text.title"
          lineHeight="1.25"
          letterSpacing="-0.02em"
        >
          {title}
        </Text>
        <Text
          mt="0.35rem"
          fontSize={{ base: "0.75rem", tablet: "0.78rem", desktop: "0.85rem" }}
          lineHeight="1.6"
          color="greyscale.text.body"
        >
          {description}
        </Text>
      </Box>
    </Box>
  );
}
